import { useEffect } from "react";

import { Alert, Button, Col, Modal, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";


import { InputCreatable } from "@/common/components/form/InputCreatable.tsx";
import SelectCreatableForm from "@/common/components/form/SelectCreatableForm.tsx";
import Loader from "@/common/components/Loader.tsx";
import {
  getNotificationRecipients,
  updateNotificationRecipients,
} from "@/modules/order/services";
import { NotificationRecipient } from "@/modules/serviceOrdering/reducers";


export type NotificationRecipientsForm = {
  tenantEmails?: string;
  tenantPhoneNumbers?: string;
  managerEmails?: string;
  operatorEmails?: string;
};

export const OrderNotificationRecipientsFormModal = ({
  serviceId,
  show,
  onHide,
}: {
  serviceId: string;
  show: boolean;
  onHide: () => void;
}) => {
  const emailSeparatorPattern = {
    pattern: {
      message: "Email invalide. Veuillez saisir un email valide",
      value: /^([^,]+@[^,]+\.[^,]+,?)*$/,
    },
  };
  const phoneNumberSeparatorPattern = {
    pattern: {
      message:
        "Numéro de téléphone invalide. Veuillez saisir un numéro de téléphone valide",
      value: /^(\+?[0-9]+,?)*$/,
    },
  };

  const queryClient = useQueryClient();
  const { data: notificationRecipient, isLoading } = useQuery<
    NotificationRecipient,
    Error
  >(["service", "notification-recipients", serviceId], () =>
    getNotificationRecipients(serviceId),
  );

  const { control, handleSubmit, reset } = useForm<NotificationRecipientsForm>({
    defaultValues: {},
  });

  useEffect(() => {
    if (notificationRecipient) {
      reset(notificationRecipient);
    }
  }, [notificationRecipient, reset]);

  const invalidateData = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["service", "notification-recipients", serviceId],
    });
  };

  const mutationUpdateNotificationRecipients = useMutation({
    mutationFn: (recipients: NotificationRecipientsForm) =>
      updateNotificationRecipients(serviceId, recipients),
    onError: (error) => {
      console.error(error);
      toast.error(
        "Une erreur est survenue lors de la mise à jour des destinataires",
      );
      handleClose();
    },
    onSuccess: async () => {
      toast.success("Destinataires mis à jour avec succès");
      await invalidateData();
      handleClose();
    },
  });

  const onSubmit = (data: NotificationRecipientsForm) => {
    mutationUpdateNotificationRecipients.mutate(data);
  };

  const handleClose = () => {
    onHide();
  };

  return (
    <Modal
      size="lg"
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>Destinataires</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {notificationRecipient && !isLoading ? (
            <Col>
              <Row>
                <h5>Administrateurs</h5>
                <Col>
                  <SelectCreatableForm
                    label={"Emails opérateur :"}
                    control={control}
                    isMulti={true}
                    name={"operatorEmails"}
                    rules={emailSeparatorPattern}
                    options={notificationRecipient.operators || []}
                  />
                  <SelectCreatableForm
                    label={"Emails gestionnaire :"}
                    control={control}
                    isMulti={true}
                    name={"managerEmails"}
                    rules={emailSeparatorPattern}
                    options={notificationRecipient.managers || []}
                  />
                </Col>
              </Row>
              <Row
                style={{
                  marginTop: "20px",
                }}
              >
                <h5>Occupant</h5>
                <Col md={6}>
                  <InputCreatable
                    label={"Emails :"}
                    control={control}
                    name={"tenantEmails"}
                    rules={emailSeparatorPattern}
                  />
                </Col>
                <Col md={6}>
                  <InputCreatable
                    label={"Numéro de téléphone :"}
                    control={control}
                    name={"tenantPhoneNumbers"}
                    rules={phoneNumberSeparatorPattern}
                  />
                </Col>
              </Row>
            </Col>
          ) : isLoading ? (
            <Loader />
          ) : (
            <Alert variant="danger">
              Impossible de récupérer les destinataires
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Retour
          </Button>

          <Button type={"submit"}>
            <span className={"me-2"}>Modifier</span>
            <i className="bi bi-pencil" />
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
